<div class="so-input-wrapper" [class.so-input-wrapper--horizontal]="display() === 'horizontal'">
  @if (label() || actionButtons().length > 0) {
    <div class="so-input-label-row">
      @if (label()) {
        <label class="so-input-label" [class.so-input-label--inline]="display() === 'horizontal'" [style]="labelStyle()">
          {{ label() }}
          @if (effectiveRequired()) {
            <span class="so-input-required">*</span>
          }
        </label>
      }
      
      @if (actionButtons().length > 0) {
        <div class="so-input-action-buttons">
          @for (button of actionButtons(); track $index) {
            @if (button.show ?? true) {
              <div class="so-input-button-wrapper">
                @if (button.isLoadingAI) {
                  <so-ai-loader-ui [size]="24" class="pe-4"/>
                } @else {
                  <so-button-gradient-ui
                    [text]="''"
                    [icon]="button.icon"
                    [variant]="button.variant || 'outline'"
                    [color]="button.color || 'secondary'"
                    [height]="32"
                    (clicked)="button.onClick()" />
                }
              </div>
            }
          }
        </div>
      }
    </div>
  }

  <div 
    class="so-input-container"
    cdkOverlayOrigin
    #triggerOrigin="cdkOverlayOrigin"
    #triggerContainer
    [class.so-input-container--search]="!isTextarea() && effectiveType() === 'search'"
    [class.so-input-container--with-prefix]="showPrefix() && prefix()"
    [class.so-input-container--with-inline-actions]="inlineActionButtons().length > 0"
  >
    @if (isTextarea()) {
      <textarea
        #textareaElement
        class="so-input-field so-input-textarea"
        [placeholder]="placeholder()"
        [value]="internalValue()"
        [disabled]="effectiveDisabled()"
        [attr.maxlength]="maxlength()"
        [attr.minlength]="minlength()"
        [rows]="rows()"
        [style]="customStyle()"
        [attr.autocomplete]="autocomplete()"
        (input)="onInput($event)"
        (keypress)="onKeyPress($event)"
        (paste)="onPaste($event)"
        (focus)="onFocus()"
        (blur)="onBlur()"
      ></textarea>
    } @else {
      @if (effectiveType() === 'search') {
        <span class="so-input-search-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="11" cy="11" r="6"/>
            <line x1="16" y1="16" x2="21" y2="21"/>
          </svg>
        </span>
      }
      @if (showPrefix() && prefix()) {
        <span class="so-input-prefix" aria-hidden="true">
          @if (prefixType() === 'icon') {
            <span class="so-input-prefix-icon material-icons">{{ prefix() }}</span>
          } @else {
            <span class="so-input-prefix-text">{{ prefix() }}</span>
          }
        </span>
      }
      @if (prefixType() === 'dropdown' && prefixOptions().length > 0) {
        <div class="so-input-prefix-dropdown">
          <button 
            type="button" 
            class="so-input-prefix-dropdown-trigger"
            (click)="togglePrefixDropdown()"
          >
            <span>{{ selectedPrefixLabel() }}</span>
            <svg class="so-input-prefix-dropdown-arrow" [class.open]="isPrefixDropdownOpen()" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          @if (isPrefixDropdownOpen()) {
            <div class="so-input-prefix-dropdown-menu">
              @for (option of prefixOptions(); track option.key) {
                <button 
                  type="button"
                  class="so-input-prefix-dropdown-item"
                  [class.active]="option.key === (internalSelectedPrefixKey() || selectedPrefixKey() || prefixOptions()[0].key)"
                  (click)="selectPrefixOption(option)"
                >
                  {{ option.label }}
                </button>
              }
            </div>
          }
        </div>
      }
      <input
        #inputElement
        class="so-input-field"
        [type]="effectiveType()"
        [placeholder]="placeholder()"
        [value]="internalValue()"
        [disabled]="effectiveDisabled()"
        [attr.maxlength]="maxlength()"
        [attr.minlength]="minlength()"
        [max]="maxNumber()"
        [min]="minNumber()"
        [style]="customStyle()"
        [style.--so-input-height.px]="inputHeight()"
        [attr.autocomplete]="autocomplete()"
        (input)="onInput($event)"
        (keypress)="onKeyPress($event)"
        (keydown)="onKeydown($event)"
        (paste)="onPaste($event)"
        (focus)="onFocus()"
        (blur)="onBlur()"
      >
      
      @if (isPassword()) {
        <button 
          type="button" 
          class="so-input-toggle-password"
          (click)="togglePasswordVisibility()"
          tabindex="-1"
        >
          @if (showPassword()) {
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
          } @else {
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          }
        </button>
      }

      @if (inlineActionButtons().length > 0) {
        <div class="so-input-inline-actions">
          @for (button of inlineActionButtons(); track $index) {
            @if (button.show ?? true) {
              @if (button.isLoadingAI) {
                <so-ai-loader-ui [size]="20"/>
              } @else {
                <so-button-gradient-ui
                  [text]="''"
                  [icon]="button.icon"
                  [variant]="button.variant || 'outline'"
                  [color]="button.color || 'secondary'"
                  (clicked)="button.onClick()" />
              }
            }
          }
        </div>
      }
    }

    <!-- Virtual scroll suggestions panel -->
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="triggerOrigin"
      [cdkConnectedOverlayOpen]="shouldUseVirtualScroll() && isPanelOpen()"
      [cdkConnectedOverlayPositions]="overlayPositions"
      [cdkConnectedOverlayWidth]="triggerWidth()"
      (overlayOutsideClick)="onOverlayOutsideClick()"
      (attach)="onOverlayAttach()"
      (detach)="onOverlayDetach()"
    >
      <div class="so-input-suggestions-panel">
        @if (filteredSuggestions().length === 0) {
         @if (showNoSuggestionsMessage()) {
          <div class="so-input-suggestion so-input-suggestion--empty">
            Sin resultados
          </div>
         }
        } @else {
          <cdk-virtual-scroll-viewport
            class="so-input-suggestions-viewport"
            [itemSize]="virtualScrollItemSize()"
            [style.maxHeight.px]="virtualScrollMaxHeight()"
            [style.height.px]="viewportHeight()"
          >
            <div
              class="so-input-suggestion"
              *cdkVirtualFor="let item of filteredSuggestions(); let i = index; trackBy: trackBySuggestion"
              [class.active]="i === activeIndex()"
              (mousedown)="onSuggestionMouseDown($event)"
              (click)="onSuggestionClick(item)"
            >
              {{ item.value }}
            </div>
          </cdk-virtual-scroll-viewport>
        }
      </div>
    </ng-template>
  </div>
</div>
